import React, { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover from '@components/CustomPopover'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import Button from '@components/formControls/Button'
import SavedSearchesOverview from '@modules/savedSearch/components/SavedSearchesOverview'
import { SavedSearch } from '@modules/savedSearch/types'
import searchApi from '@modules/search/api'
import { ProfileType } from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { useQuery } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import { PROFILE_TYPES, PROFILE_TYPE_LABELS } from '../../constants'
import SearchAutocomplete from './SearchAutocomplete'
import {
  ProfileTypeFilterItemWrapper,
  ProfileTypeFiltersContainer,
  SavedSearchBtn,
  SearchCountBadge,
  SearchIconWrapper,
  SearchInputWrapper,
  SearchSectionWrapper,
  SearchStartRowContainer,
  StyledCheckbox,
  StyledFormControlLabel,
} from './index.styles'

interface SearchSectionProps {
  searchString: string
  handleSearchClick: (searchStr: string) => void
  handleResetAll: () => void
  handleSetSearchableProfiles: (profileType: ProfileType, enabled: boolean) => void
  handleSetAllSearchableProfiles: (value: boolean) => void
  isAllProfilesEnabled: boolean
  searchableProfilesMap: Record<string, boolean>
  selectedFiltersByProfiles?: Nullable<{ [key: string]: unknown[] }>
  savedSearches?: SavedSearch[]
  setSavedSearch?: (savedSearch: Nullable<SavedSearch>) => void
  appliedSavedSearchName?: string
}

const SearchSection = (props: SearchSectionProps) => {
  const {
    searchString,
    handleSearchClick,
    handleResetAll,
    handleSetSearchableProfiles,
    handleSetAllSearchableProfiles,
    isAllProfilesEnabled,
    searchableProfilesMap,
    selectedFiltersByProfiles,
    savedSearches,
    setSavedSearch,
    appliedSavedSearchName,
  } = props

  const [disablePredictiveSearch, setDisablePredictiveSearch] = useState(false)
  const [localSearchStr, setLocalSearchStr] = useState(searchString || '')
  const debouncedValue = useDebouncedValue(localSearchStr, 300)

  const { t } = useTranslation()
  const theme = useTheme()
  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [savedSearchesOverviewAnchor, setSavedSearchesOverviewAnchor] = useState<Nullable<HTMLElement>>(null)

  const [savedSearchButton, setSavedSearchButton] = useState<Nullable<HTMLElement>>(null)

  const { hasAccessToPredictiveSearch, hasAccessToSavedSearch } = useFeatureFlags()

  const isSavedSearchedButtonEnabled = !!savedSearches?.length

  const { get, clearOneSilently } = useQueryParams()
  const savedSearchOpenQsKey = 'saved-search-open'
  const savedSearchOpenQsValue = get(savedSearchOpenQsKey)

  useEffect(() => {
    if (savedSearchOpenQsValue && isSavedSearchedButtonEnabled && savedSearchButton) {
      clearOneSilently(savedSearchOpenQsKey)
      setSavedSearchesOverviewAnchor(savedSearchButton as HTMLElement)
    }
  }, [savedSearchOpenQsValue, isSavedSearchedButtonEnabled, savedSearchButton])

  const { data: { terms, companies } = {} } = useQuery({
    queryKey: [searchApi.getPredictiveSearch.key, debouncedValue],
    queryFn: () => searchApi.getPredictiveSearch.fn(debouncedValue),
    enabled: debouncedValue?.length > 1 && !disablePredictiveSearch && hasAccessToPredictiveSearch,
  })

  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDisablePredictiveSearch(false)
    setLocalSearchStr(e.target?.value || '')
  }, [])

  const onSelectPredictiveSearch = useCallback((value: string) => {
    setDisablePredictiveSearch(true)

    if (value) {
      setLocalSearchStr(value)
      onSearchClick(value?.includes(' ') ? `"${value}"` : value)
      return
    }

    onSearchClick()
  }, [])

  const onSearchClick = useCallback((value?: string) => handleSearchClick(value ?? localSearchStr), [localSearchStr])

  const onResetSearchString = useCallback(() => {
    setLocalSearchStr('')
  }, [setLocalSearchStr])

  const onResetAll = useCallback(() => {
    handleResetAll()
    onResetSearchString()
  }, [handleResetAll])

  const onSetAllSearchableProfiles = useCallback(
    () => handleSetAllSearchableProfiles(!isAllProfilesEnabled),
    [isAllProfilesEnabled],
  )
  const resetSearchName = () => {
    setSavedSearch?.(null)
  }

  useEffect(() => {
    if (searchString === localSearchStr) {
      return
    }

    setLocalSearchStr(searchString)
  }, [searchString])

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return
    }

    onSearchClick()
  }
  const newSavedSearchesCount =
    savedSearches?.reduce((acc, search) => {
      return (search.newItemsCount ?? 0) > 0 ? acc + 1 : acc
    }, 0) ?? 0
  const badgeContent = newSavedSearchesCount > 99 ? '99+' : newSavedSearchesCount.toString()

  const handleSavedSearchesOverviewClick = (e: React.MouseEvent<HTMLElement>) => {
    setSavedSearchesOverviewAnchor(e.currentTarget as HTMLDivElement)
  }

  const savedSearchesBtnTitle = appliedSavedSearchName ? (
    <CssTruncation>{appliedSavedSearchName}</CssTruncation>
  ) : (
    t(translationKeys.SAVED_SEARCHES)
  )

  return (
    <SearchSectionWrapper>
      <SearchStartRowContainer>
        <SearchInputWrapper>
          <SearchAutocomplete
            localSearchStr={localSearchStr}
            terms={terms}
            companies={companies}
            onKeyPress={onKeyPress}
            onSearch={onSearch}
            onSelectPredictiveSearch={onSelectPredictiveSearch}
            onClear={onResetSearchString}
          />
          <SearchIconWrapper onClick={() => onSearchClick()} data-testid="search_icon">
            <SearchIcon htmlColor={colors.white} fontSize="inherit" />
          </SearchIconWrapper>
        </SearchInputWrapper>
        {!isMdScreen && (
          <>
            <Button onClick={onResetAll} data-testid="reset_all_btn" size="large" variant="contained">
              {t(translationKeys.SEARCH_RESET_SEARCH_BUTTON)}
            </Button>

            {hasAccessToSavedSearch && (
              <SavedSearchBtn
                ref={setSavedSearchButton}
                disabled={!isSavedSearchedButtonEnabled}
                saveSearchActive={!!appliedSavedSearchName}
                endIcon={<ExpandMoreIcon color={appliedSavedSearchName ? 'primary' : undefined} />}
                size="medium"
                variant="outlined"
                onClick={handleSavedSearchesOverviewClick}
              >
                {savedSearchesBtnTitle}
                {newSavedSearchesCount > 0 && (
                  <SearchCountBadge overlap="circular" color="secondary" badgeContent={badgeContent} />
                )}
              </SavedSearchBtn>
            )}
          </>
        )}
      </SearchStartRowContainer>

      <ProfileTypeFiltersContainer>
        <ProfileTypeFilterItemWrapper>
          <StyledFormControlLabel
            labelPlacement="start"
            control={
              <StyledCheckbox
                checked={isAllProfilesEnabled}
                onChange={onSetAllSearchableProfiles}
                color="primary"
                inputProps={{
                  'data-testid': 'all_profiles_checkbox',
                }}
                onKeyPress={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter') {
                    onSetAllSearchableProfiles()
                  }
                }}
              />
            }
            label={<Typography variant="body1">{t(translationKeys.SEARCH_FILTER_BY_ALL_CATEGORIES)}</Typography>}
          />
        </ProfileTypeFilterItemWrapper>

        {Object.values(PROFILE_TYPES).map(profileType => (
          <ProfileTypeFilterItemWrapper key={profileType}>
            <StyledFormControlLabel
              value={profileType}
              labelPlacement="start"
              control={
                <StyledCheckbox
                  checked={searchableProfilesMap[profileType]}
                  onChange={() => handleSetSearchableProfiles(profileType, !searchableProfilesMap[profileType])}
                  disabled={!selectedFiltersByProfiles}
                  color="primary"
                  inputProps={{
                    'data-testid': `${profileType}_profile_checkbox`,
                  }}
                  onKeyPress={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      handleSetSearchableProfiles(profileType, !searchableProfilesMap[profileType])
                    }
                  }}
                />
              }
              label={<Typography variant="body1">{PROFILE_TYPE_LABELS[profileType]}</Typography>}
            />
          </ProfileTypeFilterItemWrapper>
        ))}
      </ProfileTypeFiltersContainer>
      {isMdScreen && (
        <Box display="flex" alignItems="center">
          <Button onClick={onResetAll} data-testid="reset_all_btn" size="large">
            {t(translationKeys.SEARCH_RESET_SEARCH_BUTTON)}
          </Button>
          {hasAccessToSavedSearch && (
            <SavedSearchBtn
              ref={setSavedSearchButton}
              disabled={!isSavedSearchedButtonEnabled}
              saveSearchActive={!!appliedSavedSearchName}
              endIcon={<ExpandMoreIcon color={appliedSavedSearchName ? 'primary' : undefined} />}
              size="medium"
              variant="outlined"
              onClick={handleSavedSearchesOverviewClick}
            >
              {savedSearchesBtnTitle}
              {newSavedSearchesCount > 0 && <SearchCountBadge overlap="circular" color="secondary" badgeContent={badgeContent} />}
            </SavedSearchBtn>
          )}
        </Box>
      )}
      {savedSearchesOverviewAnchor && (
        <CustomPopover
          sx={{ marginTop: '10px' }}
          id="saved-searches-overview"
          anchor={savedSearchesOverviewAnchor}
          setAnchor={setSavedSearchesOverviewAnchor}
        >
          <SavedSearchesOverview
            appliedSavedSearchName={appliedSavedSearchName}
            resetSearchName={resetSearchName}
            onSearchSelected={setSavedSearch}
            close={() => setSavedSearchesOverviewAnchor(null)}
          />
        </CustomPopover>
      )}
    </SearchSectionWrapper>
  )
}

export default memo(SearchSection)
